import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { useCallback } from "react";
import type { ServiceType } from "@/types";
import { useAppDispatch, useAppSelector } from "./hooks";
import type { RootState } from "./store";

type FRCustomer = {
  customerID?: number | null;
  phone1: string;
  fname?: string;
  lname?: string;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  zip?: string | null;
  notes?: string;
  officeID?: number | null;
};

type FROffice = {
  officeID: number;
  officeName: string;
  timeZone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  officeLatitude: number;
  officeLongitude: number;
};

interface FieldRoutesState {
  serviceType: ServiceType | null;
  customerInfo: FRCustomer | null;
  showAddCustomerForm: boolean;
  appointmentJobId: number | null;
  officeInfo: FROffice | null;
}

const initialState: FieldRoutesState = {
  serviceType: null,
  customerInfo: null,
  showAddCustomerForm: false,
  appointmentJobId: null,
  officeInfo: null,
};

const fieldRoutesSlice = createSlice({
  name: "fieldRoutes",
  initialState,
  reducers: {
    setServiceType: (state, action: PayloadAction<ServiceType | null>) => {
      state.serviceType = action.payload;
    },
    setCustomerInfo: (state, action: PayloadAction<FRCustomer | null>) => {
      state.customerInfo = action.payload;
    },
    setShowAddCustomerForm: (state, action: PayloadAction<boolean>) => {
      state.showAddCustomerForm = action.payload;
    },
    setAppointmentJobId: (state, action: PayloadAction<number>) => {
      state.appointmentJobId = action.payload;
    },
    setOfficeInfo: (state, action: PayloadAction<FROffice | null>) => {
      state.officeInfo = action.payload;
    },
  },
});

const {
  setServiceType,
  setCustomerInfo,
  setShowAddCustomerForm,
  setAppointmentJobId,
  setOfficeInfo,
} = fieldRoutesSlice.actions;

const selectServiceType = (state: RootState) => state.fieldRoutes.serviceType;
const selectCustomerInfo = (state: RootState) => state.fieldRoutes.customerInfo;
const selectShowAddCustomerForm = (state: RootState) =>
  state.fieldRoutes.showAddCustomerForm;
const selectAppointmentJobId = (state: RootState) =>
  state.fieldRoutes.appointmentJobId;
const selectOfficeInfo = (state: RootState) => state.fieldRoutes.officeInfo;

export const { reducer } = fieldRoutesSlice;

export const useFieldRoutesIntegration = () => {
  const dispatch = useAppDispatch();
  return {
    serviceType: useAppSelector(selectServiceType),
    setServiceType: useCallback(
      (serviceType: ServiceType | null) =>
        dispatch(setServiceType(serviceType)),
      [dispatch],
    ),
    customerInfo: useAppSelector(selectCustomerInfo),
    setCustomerInfo: useCallback(
      (customerInfo: FRCustomer | null) =>
        dispatch(setCustomerInfo(customerInfo)),
      [dispatch],
    ),
    showAddCustomerForm: useAppSelector(selectShowAddCustomerForm),
    setShowAddCustomerForm: useCallback(
      (showAddCustomerForm: boolean) =>
        dispatch(setShowAddCustomerForm(showAddCustomerForm)),
      [dispatch],
    ),
    appointmentJobId: useAppSelector(selectAppointmentJobId),
    setAppointmentJobId: useCallback(
      (appointmentJobId: number) =>
        dispatch(setAppointmentJobId(appointmentJobId)),
      [dispatch],
    ),
    officeInfo: useAppSelector(selectOfficeInfo),
    setOfficeInfo: useCallback(
      (officeInfo: FROffice | null) => dispatch(setOfficeInfo(officeInfo)),
      [dispatch],
    ),
  };
};
